import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SkedMark } from "../SkedMark";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { logout } from "../../lib/api";
import { useActiveSection } from "../../lib/use-active-section";
import { navItems, SKED_CROSS_SELL_HREF, type NavItem } from "./nav-items";

interface SidebarNavProps {
  appEnv?: string;
}

// Group consecutive sub-items with the nearest preceding top-level item so
// the sidebar can render a single guide rail per group and manage
// collapse/expand state per parent.
interface NavGroup {
  parent: NavItem;
  children: NavItem[];
}

function groupNavItems(items: NavItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let current: NavGroup | null = null;
  for (const item of items) {
    if (!item.subItem) {
      current = { parent: item, children: [] };
      groups.push(current);
    } else if (current) {
      current.children.push(item);
    }
  }
  return groups;
}

function hashFromTo(to: string): string | null {
  const idx = to.indexOf("#");
  return idx === -1 ? null : to.slice(idx + 1);
}

function pathnameFromTo(to: string): string {
  const idx = to.indexOf("#");
  return idx === -1 ? to : to.slice(0, idx);
}

export function SidebarNav({ appEnv }: SidebarNavProps) {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const isStaging = appEnv && appEnv !== "production";

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
      navigate("/");
    } catch {
      setSigningOut(false);
    }
  }

  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 hidden h-dvh w-[220px] shrink-0 flex-col self-start overflow-y-auto border-r border-neutral-950/5 bg-white px-3 py-6 lg:flex"
    >
      <NavLink
        to="/"
        aria-label="Traffic Snapshot home page"
        className="flex items-center gap-2.5 rounded-md px-2 pb-6 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
      >
        <SkedMark aria-hidden="true" className="size-8 shrink-0" />
        <span className="font-display text-sm font-bold tracking-tight text-ink">
          Traffic Snapshot
        </span>
      </NavLink>

      <NavTree />

      <div className="mt-auto flex flex-col gap-3 pt-6">
        <SkedCrossSell />
        {isStaging ? <StagingChip /> : null}
        <Button asChild variant="ghost" size="sm" className="justify-start gap-3 px-3">
          <a href="https://skedsocial.com/contact-us" target="_blank" rel="noopener noreferrer">
            <i
              className="fa-regular fa-circle-question w-4 text-center text-sm"
              aria-hidden="true"
            />
            Need help?
            <i
              className="fa-regular fa-arrow-up-right-from-square ml-auto text-[10px] text-neutral-400"
              aria-hidden="true"
            />
          </a>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          disabled={signingOut}
          className="justify-start gap-3 px-3"
        >
          <i
            className="fa-regular fa-arrow-right-from-bracket w-4 text-center text-sm"
            aria-hidden="true"
          />
          {signingOut ? "Signing out…" : "Sign out"}
        </Button>
      </div>
    </nav>
  );
}

export function NavTree({ onNavigate }: { onNavigate?: () => void } = {}) {
  const groups = useMemo(() => groupNavItems(navItems), []);
  const location = useLocation();

  // Observe every hash anchor across the whole nav so the active sub-item
  // highlights as the user scrolls, regardless of which parent they belong
  // to. Hashes are unique per page, so sharing a single observer works.
  const hashIds = useMemo(
    () =>
      groups.flatMap((g) =>
        g.children.map((c) => hashFromTo(c.to)).filter((id): id is string => id !== null),
      ),
    [groups],
  );
  const activeHash = useActiveSection(hashIds);

  // Per-parent collapse state. Default: expanded when the user is on any
  // of the parent's / children's routes; collapsed otherwise. Toggling
  // persists only within the mounted lifetime — a fresh visit opens the
  // relevant parent again based on route.
  const [toggleState, setToggleState] = useState<Record<string, boolean>>({});

  function isOnRoute(pathname: string, exact: boolean): boolean {
    if (exact) return location.pathname === pathname;
    return location.pathname === pathname || location.pathname.startsWith(pathname + "/");
  }

  function isParentActive(group: NavGroup): boolean {
    const parentPath = pathnameFromTo(group.parent.to);
    if (isOnRoute(parentPath, !!group.parent.end)) return true;
    return group.children.some((c) => isOnRoute(pathnameFromTo(c.to), false));
  }

  return (
    <ul role="list" className="flex flex-col gap-1">
      {groups.map((group) => {
        if (group.parent.disabled) {
          return (
            <li key={group.parent.to}>
              <DisabledItem item={group.parent} />
            </li>
          );
        }
        const parentActive = isParentActive(group);
        const defaultOpen = parentActive && group.children.length > 0;
        const explicit = toggleState[group.parent.to];
        const open = explicit === undefined ? defaultOpen : explicit;
        const hasChildren = group.children.length > 0;
        return (
          <li key={group.parent.to}>
            <TopLevelItem
              item={group.parent}
              hasChildren={hasChildren}
              open={open}
              onToggle={
                hasChildren
                  ? () => setToggleState((prev) => ({ ...prev, [group.parent.to]: !open }))
                  : undefined
              }
              onNavigate={onNavigate}
            />
            {hasChildren && open ? (
              <SubNavGroup
                items={group.children}
                activeHash={activeHash}
                parentActive={parentActive}
                onNavigate={onNavigate}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function TopLevelItem({
  item,
  hasChildren,
  open,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  hasChildren: boolean;
  open: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex min-w-0 flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            "focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2",
            isActive
              ? "bg-purple-50 text-purple-700"
              : "text-neutral-700 hover:bg-neutral-50 hover:text-neutral-950",
          )
        }
      >
        <i className={cn(item.icon, "w-4 shrink-0 text-center text-sm")} aria-hidden="true" />
        <span className="truncate">{item.label}</span>
      </NavLink>
      {hasChildren && onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          aria-expanded={open}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-50 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
        >
          <i
            className={cn(
              "fa-solid fa-chevron-down text-[10px] transition-transform",
              open ? "rotate-180" : "",
            )}
            aria-hidden="true"
          />
        </button>
      ) : null}
    </div>
  );
}

function SubNavGroup({
  items,
  activeHash,
  parentActive,
  onNavigate,
}: {
  items: NavItem[];
  activeHash: string | null;
  parentActive: boolean;
  onNavigate?: () => void;
}) {
  return (
    <div className="ml-[19px] mt-1 mb-1 border-l border-neutral-200">
      <ul role="list" className="flex flex-col">
        {items.map((item) => {
          const itemHash = hashFromTo(item.to);
          // Only treat the sub-item as active when the parent route is
          // also active — matching hash on a different page shouldn't
          // highlight something in the current sidebar.
          const hashActive = parentActive && itemHash !== null && activeHash === itemHash;
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) => {
                  const linkActive = hashActive || (isActive && itemHash === null);
                  return cn(
                    "relative flex items-center py-1.5 pl-4 pr-3 text-xs font-medium transition-colors",
                    "focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2",
                    linkActive ? "text-purple-700" : "text-neutral-500 hover:text-neutral-900",
                  );
                }}
              >
                {({ isActive }) => {
                  const linkActive = hashActive || (isActive && itemHash === null);
                  return (
                    <>
                      {linkActive ? (
                        <span
                          aria-hidden="true"
                          className="absolute inset-y-1 -left-px w-0.5 rounded-full bg-purple-500"
                        />
                      ) : null}
                      <span className="truncate">{item.label}</span>
                    </>
                  );
                }}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DisabledItem({ item }: { item: NavItem }) {
  return (
    <span
      aria-disabled="true"
      title={`${item.label} ships in a later milestone`}
      className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-neutral-400"
    >
      <i className={cn(item.icon, "w-4 text-center text-sm")} aria-hidden="true" />
      <span className="flex-1">{item.label}</span>
      <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500">
        Soon
      </span>
    </span>
  );
}

export function SkedCrossSell() {
  return (
    <aside
      aria-label="About Sked Social"
      // M12.9 — sidebar cross-sell uses the Sked brand pink family
      // (#ED207C in the SkedMark heart) so it reads as marketing, not
      // as another in-product blurple panel. Kept visually consistent
      // with the inline CtaPlacement cross-sell so the user learns the
      // colour = "Sked Social is pitching you" cue.
      className="rounded-xl bg-pink-50 p-3 ring-1 ring-pink-100"
    >
      <SkedMark aria-hidden="true" className="size-5" />
      <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-pink-700">
        From Sked Social
      </p>
      <p className="mt-1.5 text-sm leading-snug text-neutral-700 text-pretty">
        Plan, schedule and publish across your social channels. Built for teams that take social
        seriously.
      </p>
      <a
        href={SKED_CROSS_SELL_HREF}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex items-center gap-1.5 rounded text-sm font-semibold text-pink-700 hover:text-pink-900 focus-visible:outline-2 focus-visible:outline-pink-500 focus-visible:outline-offset-2"
      >
        Try Sked Social
        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
      </a>
    </aside>
  );
}

function StagingChip() {
  return (
    <div className="rounded-lg bg-neutral-25 p-3 ring-1 ring-neutral-950/5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-purple-700">Staging</p>
      <p className="mt-1 text-sm leading-snug text-neutral-500 text-pretty">
        Development build. GA4 data is live; no UTM warehouse.
      </p>
    </div>
  );
}
